import { createFileRoute, Link } from "@tanstack/react-router";
import format from "date-fns/format";
import { RssIcon } from "lucide-react";

import { createHead } from "@/components/createHead";
import { Page } from "@/components/Page";
import { ButtonLink } from "@/components/ui/button";
import { P } from "@/components/ui/typography";
/* eslint-disable import-x/no-duplicates */
// @ts-expect-error TODO: fix this
import circle1 from "@/images/illustration.png?slice=1&height=400&width=600&x=161&y=0";
// @ts-expect-error TODO: fix this
import circle2 from "@/images/illustration.png?slice=1&height=400&width=600&x=500&y=1376";
/* eslint-enable import-x/no-duplicates */
import { getPosts } from "@/lib/content";

export const Route = createFileRoute("/_marketing-header-layout/blog")({
  component: RouteComponent,

  loader: async () => {
    const posts = await getPosts();
    return { posts };
  },
  head: ({ match: { pathname } }) =>
    createHead({
      title: "Blog | Graze",
      description:
        "Keep up to date with the latest news and updates from Graze",
      ogTitle: "Blog",
      ogImage: "/blog/og.png",
      pathname,
    }),
});

function RouteComponent() {
  const { posts: allPosts } = Route.useLoaderData();
  const [firstPost, ...posts] = allPosts;

  if (!firstPost) {
    return <div>No posts found</div>;
  }

  return (
    <Page className="relative z-0">
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="mb-2 md:mb-8 flex justify-between items-center @container">
          <div className="flex items-center gap-10">
            <div
              className="sharp-grotesk text-[20cqw] leading-[0.9] rounded-full h-64 w-64 fixed top-20 right-4 z-[-1] blog-circle border border-slate-5 dark:border-slatedark-6 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${circle1}')`,
                backgroundSize: "100cqw",
                boxShadow: "inset 0 0 12px rgba(0, 0, 0, 0.4)",
              }}
            ></div>
            <div
              className="sharp-grotesk text-[20cqw] leading-[0.9] rounded-full h-64 w-64 fixed top-[60vh] left-4 z-[-1] blog-circle border border-slate-5 dark:border-slatedark-6 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${circle2}')`,
                backgroundSize: "100cqw",
                boxShadow: "inset 0 0 12px rgba(0, 0, 0, 0.4)",
              }}
            ></div>
            <h1 className="text-4xl md:text-6xl font-bold instrument-serif">
              Latest Blog Posts
            </h1>
          </div>
          <ButtonLink to="/blog/rss.xml" variant="ghost" size="icon">
            <span className="sr-only">RSS Feed</span>
            <RssIcon className="h-4 w-4 text-orange-10" />
          </ButtonLink>
        </div>
      </div>

      <Link
        to="/blog/$slug"
        params={{ slug: firstPost.slug.current }}
        className="grid grid-cols-1 items-center md:grid-cols-[40%_1fr] bg-yellow-2 dark:bg-slatedark-3 hover:bg-slate-3 dark:hover:bg-yellowdark-4 border-2 rounded-lg border-slate-8 dark:border-slatedark-8 shadow-xs cursor-pointer-extra transition-transform hover:scale-103"
      >
        <div className="relative overflow-hidden self-stretch">
          <img
            src={firstPost.image}
            alt={firstPost.title}
            className="object-cover h-full w-full z-1 relative"
          />
        </div>
        <div className="flex flex-col gap-3 md:gap-6 p-8 border-l border-slate-8 dark:border-slatedark-8">
          <div className="flex items-start gap-2 justify-between">
            <h2 className="scroll-m-20 instrument-serif text-2xl font-extrabold tracking-tight lg:text-6xl text-teal-11 dark:text-tealdark-11">
              {firstPost.title}
            </h2>
            <div className="text-sm md:text-base text-slate-dim font-light">
              {format(new Date(firstPost._createdAt), "MMMM dd, yyyy")}
            </div>
          </div>

          <P className="md:text-xl">{firstPost.description}</P>

          <div className="flex gap-4 items-center flex-1 text-xl instrument-serif">
            <div className="flex gap-2">
              <span className="font-semibold">~ {firstPost.author.name}</span>
              <span className="text-slate-dim">@{firstPost.author.handle}</span>
            </div>
          </div>
        </div>
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {posts.map((post) => (
          <Link
            key={post.slug.current}
            to="/blog/$slug"
            params={{ slug: post.slug.current }}
            className="flex bg-yellow-2 dark:bg-slatedark-3 hover:bg-slate-3 dark:hover:bg-yellowdark-4 border-2 rounded-lg border-slate-8 dark:border-slatedark-8 shadow-xs cursor-pointer-extra @container transition-transform hover:scale-103"
          >
            <div className="aspect-square max-w-[28cqw]">
              <img
                src={post.image}
                alt={post.title}
                className="object-cover h-full w-full"
              />
            </div>

            <div className="flex flex-col gap-4 p-4 border-l border-slate-8 dark:border-slatedark-8 flex-1">
              <h2 className="scroll-m-20 instrument-serif text-3xl font-extrabold tracking-tight lg:text-4xl text-teal-11 dark:text-tealdark-11">
                {post.title}
              </h2>

              {post.description && (
                <P className="md:text-lg flex-1">{post.description}</P>
              )}

              <div className="flex items-center gap-2 justify-between">
                <div className="text-sm text-slate-dim font-light">
                  {format(new Date(post._createdAt), "MMMM dd, yyyy")}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Page>
  );
}
